import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { keyframes } from '@mui/material/styles';
import { searchWatchlistSecurityDiscovery } from '../../utils/ApiCall';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../shared/securityDisplayPrimitives';
import {
    ADD_DIALOG_TOOLBAR_HEIGHT,
    CountryCodeAutocomplete,
    addDialogFieldSx,
    addDialogStaticLabelSx,
    addDialogToggleSx,
    addDialogToolbarFieldSx,
} from './WatchlistAddSecurityPrimitives';
import {
    SEARCH_RESULT_LIMIT,
    isValidDiscoveryResponse,
    orderSearchResults,
} from './securityAdditionContract';
import { PICK_OUTCOME, pickFromDiscoveryResult } from './add_security/pendingAdditions';

const DEBOUNCE_MS = 200;
// US listings first, then the rest, each by ticker (orderSearchResults).
const DEFAULT_RESULT_SORT = 'us_first';

const basketConfirmationFade = keyframes`
    0%, 55% {
        color: #2e7d32;
        opacity: 1;
    }
    100% {
        color: #81c784;
        opacity: 0;
    }
`;

function normalizeQuery(value) {
    return String(value || '').normalize('NFC').trim();
}

// Outcomes that leave the chosen listing in the basket.
const IN_BASKET_OUTCOMES = new Set([
    PICK_OUTCOME.ADDED,
    PICK_OUTCOME.REPLACED,
    PICK_OUTCOME.DUPLICATE,
]);

// The Search securities tab of Build Watchlist additions. The host dialog
// owns the basket both tabs fill: `onPick(result)` adds a result and returns
// its PICK_OUTCOME, `picks` marks the chosen listings, and
// `remainingCapacity` keeps a new security out of a full basket.
// `onCapacityChange` reports the capacity each search returns.
export default function WatchlistTickerBuilder({
    active,
    busy,
    picks,
    remainingCapacity,
    onPick,
    onCapacityChange,
}) {
    const [query, setQuery] = useState('');
    const [country, setCountry] = useState('');
    const [status, setStatus] = useState('idle');
    const [results, setResults] = useState([]);
    const [searchError, setSearchError] = useState('');
    const [retryVersion, setRetryVersion] = useState(0);
    const [exactTickerOnly, setExactTickerOnly] = useState(false);
    const [resultSort, setResultSort] = useState(DEFAULT_RESULT_SORT);
    const [basketFeedback, setBasketFeedback] = useState(null);
    const generationRef = useRef(0);
    const feedbackSequenceRef = useRef(0);
    const feedbackTimerRef = useRef(null);
    const tickerInputRef = useRef(null);

    const normalizedQuery = normalizeQuery(query);
    const ready = Array.from(normalizedQuery).length >= 2;
    const pickedSecurities = useMemo(() => new Set(picks.map((pick) => pick.key)), [picks]);
    const pickedListings = useMemo(() => new Set(picks.map((pick) => pick.listingKey)), [picks]);
    const displayedResults = useMemo(() => {
        const filtered = exactTickerOnly
            ? results.filter((result) => result.match_kind === 'exact_ticker')
            : results;
        if (resultSort === 'relevance') return filtered;
        if (resultSort === DEFAULT_RESULT_SORT) return orderSearchResults(filtered);
        const ordered = [...filtered];
        ordered.sort((left, right) => {
            const primary = resultSort === 'country'
                ? left.country.localeCompare(right.country)
                : left.ticker.localeCompare(right.ticker);
            if (primary !== 0) return primary;
            const secondary = resultSort === 'country'
                ? left.ticker.localeCompare(right.ticker)
                : left.country.localeCompare(right.country);
            if (secondary !== 0) return secondary;
            return String(left.display_name || '').localeCompare(String(right.display_name || ''));
        });
        return ordered;
    }, [exactTickerOnly, resultSort, results]);

    const flashBasketFeedback = (optionToken) => {
        if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
        feedbackSequenceRef.current += 1;
        setBasketFeedback({
            optionToken,
            sequence: feedbackSequenceRef.current,
        });
        feedbackTimerRef.current = window.setTimeout(() => {
            setBasketFeedback(null);
            feedbackTimerRef.current = null;
        }, 5000);
    };

    useEffect(() => () => {
        if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    }, []);

    useEffect(() => {
        if (active) return;
        generationRef.current += 1;
        setQuery('');
        setCountry('');
        setStatus('idle');
        setResults([]);
        setSearchError('');
        setRetryVersion(0);
        setExactTickerOnly(false);
        setResultSort(DEFAULT_RESULT_SORT);
        setBasketFeedback(null);
        if (feedbackTimerRef.current) {
            window.clearTimeout(feedbackTimerRef.current);
            feedbackTimerRef.current = null;
        }
    }, [active]);

    useEffect(() => {
        const generation = generationRef.current + 1;
        generationRef.current = generation;
        if (!active) return undefined;
        setResults([]);
        setSearchError('');
        if (!ready) {
            setStatus(normalizedQuery ? 'minimum' : 'idle');
            return undefined;
        }
        setStatus('debouncing');
        const timerId = window.setTimeout(async () => {
            setStatus('loading');
            const response = await searchWatchlistSecurityDiscovery({
                query: normalizedQuery,
                country: country || undefined,
                limit: SEARCH_RESULT_LIMIT,
            });
            if (generationRef.current !== generation) return;
            if (response?.success === false) {
                setStatus('error');
                setSearchError(
                    response?.message || 'Security search is temporarily unavailable.',
                );
                return;
            }
            if (!isValidDiscoveryResponse(response)) {
                setStatus('error');
                setSearchError('Security search returned an invalid response. Please retry.');
                return;
            }
            onCapacityChange?.(response.capacity);
            setResults(response.results);
            setStatus(response.results.length ? 'success' : 'empty');
        }, DEBOUNCE_MS);
        return () => {
            window.clearTimeout(timerId);
            if (generationRef.current === generation) generationRef.current += 1;
        };
    }, [active, country, normalizedQuery, ready, retryVersion]);

    const addResult = (result) => {
        if (busy) return;
        if (IN_BASKET_OUTCOMES.has(onPick(result))) flashBasketFeedback(result.option_token);
        tickerInputRef.current?.focus();
    };

    const statusText = (() => {
        if (status === 'minimum') return 'Enter at least 2 ticker characters or 3 company-name characters.';
        if (status === 'debouncing') return 'Waiting for more search input.';
        if (status === 'loading') return 'Searching the security catalog.';
        if (status === 'empty') return 'No recognized ticker or company matches were found.';
        if (status === 'success') {
            if (displayedResults.length !== results.length) {
                return `${displayedResults.length} of ${results.length} recognized matches shown.`;
            }
            if (results.length === SEARCH_RESULT_LIMIT) {
                return 'Showing the first 20 matches. Keep typing or choose a country to narrow the results.';
            }
            return `${results.length} recognized ${results.length === 1 ? 'match' : 'matches'}.`;
        }
        return 'Search begins after 2 ticker characters or 3 company-name characters.';
    })();

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Search by ticker symbol or company name. A catalog match still completes
                the normal checks after Review.
            </Typography>
            <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                justifyContent="flex-end"
                alignItems="flex-end"
                columnGap={1.5}
                rowGap={1}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        minHeight: ADD_DIALOG_TOOLBAR_HEIGHT,
                    }}
                >
                    Organize the current results
                </Typography>
                <Button
                    disabled={status !== 'success' || results.length < 2}
                    onClick={() => setExactTickerOnly((current) => !current)}
                    aria-pressed={exactTickerOnly}
                    startIcon={exactTickerOnly ? <CheckRoundedIcon /> : null}
                    sx={addDialogToggleSx}
                >
                    Exact ticker only
                </Button>
                <TextField
                    select
                    size="small"
                    disabled={status !== 'success' || results.length < 2}
                    label="Sort shown results"
                    value={resultSort}
                    onChange={(event) => setResultSort(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ notched: false }}
                    sx={[
                        addDialogToolbarFieldSx,
                        addDialogStaticLabelSx,
                        { flex: { xs: '1 1 180px', sm: '0 0 200px' } },
                    ]}
                >
                    <MenuItem value={DEFAULT_RESULT_SORT}>US first</MenuItem>
                    <MenuItem value="relevance">Relevance</MenuItem>
                    <MenuItem value="country">Country</MenuItem>
                    <MenuItem value="ticker">Ticker</MenuItem>
                </TextField>
            </Stack>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            >
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                        freeSolo
                        fullWidth
                        autoHighlight
                        openOnFocus
                        disableCloseOnSelect
                        disabled={busy}
                        options={displayedResults}
                        value={null}
                        inputValue={query}
                        filterOptions={(values) => values}
                        getOptionLabel={(option) => (
                            typeof option === 'string' ? option : String(option?.ticker || '')
                        )}
                        isOptionEqualToValue={(option, value) => (
                            option?.option_token === value?.option_token
                        )}
                        getOptionDisabled={(option) => (
                            busy
                            || option.already_active
                            || (
                                !pickedSecurities.has(pickFromDiscoveryResult(option)?.key)
                                && remainingCapacity <= 0
                            )
                        )}
                        loading={status === 'loading'}
                        loadingText="Searching securities…"
                        noOptionsText={ready
                            ? (
                                exactTickerOnly && results.length
                                    ? 'No exact ticker matches. Turn off Exact ticker only to see related matches.'
                                    : 'No recognized ticker or company matches'
                            )
                            : 'Enter 2 ticker or 3 company-name characters'}
                        onInputChange={(_event, value, reason) => {
                            if (busy) return;
                            if (reason === 'input' || reason === 'clear') {
                                generationRef.current += 1;
                                setQuery(value);
                            }
                        }}
                        onChange={(_event, value, reason) => {
                            if (busy) return;
                            if (reason === 'selectOption' && value && typeof value !== 'string') {
                                addResult(value);
                            }
                        }}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            const selected = pickedListings.has(
                                pickFromDiscoveryResult(option)?.listingKey,
                            );
                            const countryDisplay = getCountryChipDisplay(option.country);
                            return (
                                <Box
                                    component="li"
                                    key={option.option_token || key}
                                    {...optionProps}
                                    sx={{
                                        display: 'grid !important',
                                        gridTemplateColumns: {
                                            xs: 'minmax(52px, auto) auto minmax(0, 1fr) 28px',
                                            sm: 'minmax(64px, auto) auto minmax(160px, 1fr) minmax(120px, auto) 132px',
                                        },
                                        alignItems: 'center',
                                        columnGap: 1,
                                        minHeight: 44,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {option.ticker}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={<CountryChipLabel display={countryDisplay} />}
                                    />
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        alignItems="center"
                                        sx={{ minWidth: 0 }}
                                    >
                                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                            {option.display_name || 'Company name unavailable'}
                                        </Typography>
                                        {option.already_active && (
                                            <Chip
                                                size="small"
                                                color="info"
                                                label="Already in Watchlist"
                                                sx={{ flexShrink: 0 }}
                                            />
                                        )}
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                        sx={{ display: { xs: 'none', sm: 'block' } }}
                                    >
                                        {[option.instrument_type, option.exchange, option.mic]
                                            .filter(Boolean).join(' · ')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: { xs: 28, sm: 132 },
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: 0.5,
                                        }}
                                    >
                                        {basketFeedback?.optionToken === option.option_token && (
                                            <Typography
                                                key={basketFeedback.sequence}
                                                role="status"
                                                aria-live="polite"
                                                variant="caption"
                                                sx={{
                                                    display: { xs: 'none', sm: 'block' },
                                                    flex: 1,
                                                    fontWeight: 700,
                                                    textAlign: 'right',
                                                    whiteSpace: 'nowrap',
                                                    animation: `${basketConfirmationFade} 5s ease-out forwards`,
                                                }}
                                            >
                                                Added to basket!
                                            </Typography>
                                        )}
                                        {selected && (
                                            <Tooltip title="Added to basket" placement="left">
                                                <CheckCircleRoundedIcon
                                                    color="success"
                                                    fontSize="small"
                                                    aria-label="Added to basket"
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Ticker or company"
                                inputRef={tickerInputRef}
                                inputProps={{
                                    ...params.inputProps,
                                    maxLength: 128,
                                    'aria-label': 'Ticker or company',
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRoundedIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <>
                                            {status === 'loading' && <CircularProgress size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={addDialogFieldSx}
                            />
                        )}
                    />
                    <Typography
                        role="status"
                        aria-live="polite"
                        variant="caption"
                        color="text.secondary"
                        sx={{ pl: 0.5 }}
                    >
                        {statusText}
                    </Typography>
                </Stack>
                <CountryCodeAutocomplete
                    label="Country filter (optional)"
                    value={country}
                    disabled={busy}
                    onChange={(nextCountry) => {
                        if (busy) return;
                        generationRef.current += 1;
                        setCountry(nextCountry);
                    }}
                    sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}
                />
            </Stack>
            {searchError && (
                <Alert
                    severity="warning"
                    action={(
                        <Button
                            color="inherit"
                            size="small"
                            disabled={busy}
                            onClick={() => {
                                if (busy) return;
                                generationRef.current += 1;
                                setRetryVersion((current) => current + 1);
                            }}
                        >
                            Retry
                        </Button>
                    )}
                >
                    {searchError}
                </Alert>
            )}
        </Stack>
    );
}

WatchlistTickerBuilder.propTypes = {
    active: PropTypes.bool.isRequired,
    busy: PropTypes.bool.isRequired,
    picks: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        listingKey: PropTypes.string.isRequired,
    })).isRequired,
    remainingCapacity: PropTypes.number.isRequired,
    onPick: PropTypes.func.isRequired,
    onCapacityChange: PropTypes.func,
};
